import type { CSSProperties, ReactNode } from "react";
import styles from "./ds.module.css";

/*
    A table on the platform's table tokens: uppercase 10px heads, hairline rules, a sticky
    head inside its own scroll box. CSS grid, so the columns are `track`s and a row can be a
    link-like target without <tr> fighting the layout.
*/

export type ColumnTone = "id" | "mono" | "strong" | "body" | "muted";

export interface GridColumn {
    label: ReactNode;
    /** A grid track: `1fr`, `minmax(120px,1fr)`, `90px`. Defaults to `1fr`. */
    track?: string;
    align?: "left" | "right" | "center";
    tone?: ColumnTone;
    ruleLeft?: boolean;
    nowrap?: boolean;
    /** Set on a sortable column; the header becomes a button. */
    sortKey?: string;
}

const TONE: Record<ColumnTone, string> = {
    id: styles.toneId, mono: styles.toneMono, strong: styles.toneStrong, body: "", muted: styles.toneMuted,
};

export function tracksOf(columns: GridColumn[]): string {
    return columns.map((c) => c.track ?? "1fr").join(" ");
}

function cellClass(c: GridColumn): string {
    return [
        styles.td,
        c.align === "right" ? styles.right : c.align === "center" ? styles.center : "",
        TONE[c.tone ?? "body"],
        c.ruleLeft ? styles.ruleLeft : "",
        c.nowrap ? styles.nowrap : "",
    ].join(" ");
}

export function GridHead({ columns, sort, onSort, style }: {
    columns: GridColumn[];
    /** The current sort: `key` ascending or `-key` descending. */
    sort?: string;
    onSort?: (sort: string) => void;
    style?: CSSProperties;
}) {
    return (
        <div className={`${styles.grid} ${styles.gridHead}`} style={{ gridTemplateColumns: tracksOf(columns), ...style }} role="row">
            {columns.map((c, i) => {
                const key = c.sortKey;
                const state = key && sort === key ? "ascending" : key && sort === `-${key}` ? "descending" : undefined;
                const cls = [styles.th, c.align === "right" ? styles.right : c.align === "center" ? styles.center : "", key ? styles.thSort : "", state ? styles.thOn : "", c.ruleLeft ? styles.ruleLeft : ""].join(" ");
                return (
                    <div key={i} className={cls} role="columnheader" aria-sort={state}
                        onClick={key && onSort ? () => onSort(sort === key ? `-${key}` : key) : undefined}>
                        <span>{c.label}</span>
                        {state && <span className={styles.thArrow} aria-hidden="true">{state === "ascending" ? "↑" : "↓"}</span>}
                    </div>
                );
            })}
        </div>
    );
}

export function GridRow({ columns, cells, onClick, className, style, title }: {
    columns: GridColumn[]; cells: ReactNode[]; onClick?: () => void; className?: string; style?: CSSProperties; title?: string;
}) {
    return (
        <div className={`${styles.grid} ${styles.gridRow} ${onClick ? styles.gridRowClick : ""} ${className ?? ""}`}
            style={{ gridTemplateColumns: tracksOf(columns), ...style }} role="row" onClick={onClick} title={title}>
            {columns.map((c, i) => <div key={i} className={cellClass(c)} role="cell">{cells[i]}</div>)}
        </div>
    );
}

/**
 * The whole table: head, rows, an optional total row. `rows` are cells already rendered, so
 * a caller can put a badge, a link or server HTML in one. `rowKey` keeps React keys stable.
 */
export function GridTable({ columns, rows, rowKey, total, sort, onSort, maxHeight, minWidth, onRowClick, empty, style }: {
    columns: GridColumn[];
    rows: ReactNode[][];
    rowKey?: (row: ReactNode[], index: number) => string | number;
    total?: ReactNode[];
    sort?: string;
    onSort?: (sort: string) => void;
    maxHeight?: number | string;
    minWidth?: number | string;
    onRowClick?: (index: number) => void;
    /** Shown beneath the head when there are no rows. */
    empty?: ReactNode;
    style?: CSSProperties;
}) {
    return (
        <div className={styles.gridScroll} style={{ maxHeight, ...style }} role="table">
            <div style={{ minWidth }}>
                <GridHead columns={columns} sort={sort} onSort={onSort} />
                {rows.map((cells, i) => (
                    <GridRow key={rowKey ? rowKey(cells, i) : i} columns={columns} cells={cells}
                        onClick={onRowClick ? () => onRowClick(i) : undefined} />
                ))}
                {rows.length === 0 && empty && <div style={{ padding: "22px 14px", fontSize: "var(--fs-cell)", color: "var(--slate-500)" }}>{empty}</div>}
                {total && (
                    <div className={`${styles.grid} ${styles.gridTotal}`} style={{ gridTemplateColumns: tracksOf(columns) }} role="row">
                        {columns.map((c, i) => <div key={i} className={cellClass(c)} style={{ color: c.tone === "muted" ? undefined : "var(--slate-950)", fontWeight: 700 }} role="cell">{total[i]}</div>)}
                    </div>
                )}
            </div>
        </div>
    );
}

/** Label | value rows inside a widget, hairline-separated. A missing value reads "—" in muted ink. */
export function DefinitionRows({ rows }: { rows: { label: ReactNode; value: ReactNode; mono?: boolean; full?: boolean; empty?: boolean }[] }) {
    return (
        <div className={styles.defs}>
            {rows.map((r, i) => (
                <div key={i} className={r.full ? styles.defFull : styles.def}>
                    <div className={styles.defLabel}>{r.label}</div>
                    <div className={`${styles.defValue} ${r.empty ? styles.muted : ""} ${r.mono && !r.empty ? styles.mono : ""}`}>{r.value}</div>
                </div>
            ))}
        </div>
    );
}

/** The footer under a paged table: the range shown, a per-page select, page x of y, prev/next. */
export function Pager({ first, last, total, noun, page, pages, onPrev, onNext, perPage, perOptions, onPerPage, loading = false }: {
    first: number; last: number; total: number; noun: string;
    page: number; pages: number; onPrev: () => void; onNext: () => void;
    perPage: number; perOptions: number[]; onPerPage: (n: number) => void; loading?: boolean;
}) {
    return (
        <div className={styles.pager}>
            <span className={styles.pagerText}>
                {loading ? "Loading…" : total ? `Showing ${first.toLocaleString("en-US")}–${last.toLocaleString("en-US")} of ${total.toLocaleString("en-US")} ${noun}` : `Showing 0 ${noun}`}
            </span>
            <div className={styles.pagerRight}>
                <span className={styles.pagerText}>Per page</span>
                <div className={styles.selectWrap} style={{ width: 78 }}>
                    <select className={`${styles.control} ${styles.controlSm}`} aria-label="Per page" value={perPage} onChange={(e) => onPerPage(Number(e.target.value))}>
                        {perOptions.map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                    <span className={styles.caret} aria-hidden="true">▾</span>
                </div>
                <span className={styles.pagerText} style={{ marginLeft: 8 }}>Page {page} of {Math.max(1, pages)}</span>
                <button type="button" className={styles.iconBtn} style={{ width: 28, height: 28 }} aria-label="Previous page" title="Previous page" disabled={page <= 1} onClick={onPrev}>
                    <i className="bi bi-chevron-left" aria-hidden="true" />
                </button>
                <button type="button" className={styles.iconBtn} style={{ width: 28, height: 28 }} aria-label="Next page" title="Next page" disabled={page >= pages} onClick={onNext}>
                    <i className="bi bi-chevron-right" aria-hidden="true" />
                </button>
            </div>
        </div>
    );
}
